/**
 * ThreadListTabs — threads · mentions · direct, above the thread list
 * (2026-09-17).
 *
 * Miles asked for two ways into the list that Slack has and this did not: the
 * places he has been named, and his one-on-ones. They are a segmented strip that
 * SWAPS the list below rather than a popup over it, so nothing is covered and
 * the list keeps its own scroll.
 *
 * It sits between the header and the search box, ABOVE the box and not below it.
 * `ThreadSearch`'s results panel is in normal flow (`margin-top: 6px`,
 * `max-height: min(52vh, 460px)`), so a strip placed under it would jump down
 * the moment anyone typed — a control that moves while you reach for it. Search
 * stays global across all three tabs.
 *
 * `variant` mirrors `ThreadSearch`'s own prop, and the two mount sites it serves
 * — the fullscreen rail and `ThreadListSheet` — are between them every surface,
 * because the sheet renders both the phone sheet and the docked drop-up panel.
 *
 * Inline, never a portal: no `createPortal`, so it consumes no rung of the
 * overlay ladder and `check-overlay-layers.mjs` has nothing to say about it.
 * Both surfaces are dark, which is what makes `--brand-gold` correct AS TEXT
 * here (the gold-as-text rule is about light grounds).
 *
 * Copy is chrome, in the viewer's language, through `threadListCopy` (D9, and
 * already registered — no new copy module).
 */
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { threadListCopy } from './threadListCopy';
import type { ThreadListLang } from '../../types/threadCategories';

/** Which list the body shows. `threads` is the list that was always there. */
export type ThreadListTab = 'threads' | 'mentions' | 'direct';

export const THREAD_LIST_TABS: readonly ThreadListTab[] = ['threads', 'mentions', 'direct'];

export interface ThreadListTabsProps {
  active: ThreadListTab;
  onSelect: (tab: ThreadListTab) => void;
  /** Unread mentions. 0 hides the badge; the cap renders as "N+". */
  mentionUnread?: number;
  /** Unread across the viewer's one-on-one threads. */
  directUnread?: number;
  /**
   * The right-click Miles asked for ("they can right click and click read all").
   * Offered on the mentions tab only, and only as a SECOND way in — the visible
   * "mark all read" button in the list's own header is the one that works on a
   * phone, where there is no right button to click.
   */
  onMarkAllRead?: () => void;
  variant?: 'rail' | 'sheet';
  lang?: ThreadListLang;
}

/** Beyond this the badge stops counting and says so. */
export const TAB_BADGE_CAP = 50;

/**
 * What a thread-list surface needs to render the strip and its bodies. ONE
 * bundle, handed identically to the rail and to the sheet, because the three
 * thread surfaces are three renderers of one data source and a prop each of
 * them assembles for itself is how they start disagreeing.
 *
 * `mentionsPanel` is built by the HOST rather than by each surface: the mentions
 * list has its own controller, its own context fetches and its own read state,
 * and two surfaces building it separately would run two of each. The direct tab
 * has no such bundle — its rows are ordinary thread rows, so each surface
 * renders them with its own row anatomy, which is exactly how the existing
 * sections already work.
 */
export interface ThreadListActivityProps {
  tab: ThreadListTab;
  onSelectTab: (tab: ThreadListTab) => void;
  mentionUnread: number;
  directUnread: number;
  onMarkAllRead: () => void;
  mentionsPanel: React.ReactNode;
  lang?: ThreadListLang;
}

/**
 * At or below this much room the counts are dots; above it they are numbers.
 *
 * THE LABEL ALWAYS WINS. A count that squeezed "mentions" into "menti…" is the
 * defect this round was asked to fix, so when the strip cannot hold both, it is
 * the DIGITS that go — the word stays whole and the badge still says "3 unread"
 * to a screen reader.
 *
 * MEASURED, not derived (Chromium, 2026-09-20, worst case = a "50+" and a "9"
 * showing at once): with counts the strip first overflows at a container width
 * of 225px in English and 231px in Spanish; as dots, at 173px and 180px. So 231
 * is the last width at which a count does not fit, and the rail's own minimum
 * (220px, i.e. a 200px container) clears the dot figure with room to spare.
 * The measuring browser had no Inter and fell back to a WIDER face, so
 * production has more headroom than these numbers, never less.
 */
export const TAB_BADGE_DOT_MAX_CONTAINER_PX = 231;

/**
 * The strip's outer spacing, and the query container the badge form is
 * decided from. It measures the room the tabs actually have — which now
 * changes when someone drags the divider — rather than the viewport.
 */
const TabsRow = styled.div`
  /* Rail density (2026-09-20); the fallback is the sheet's own spacing. */
  padding: 2px var(--rail-chrome-px, 12px) var(--rail-chrome-pb, 8px) var(--rail-chrome-px, 12px);
  container-type: inline-size;
  container-name: thread-tabs;
  ${() => NarrowBadgeRules}
`;

/**
 * Its own badge rather than `ThreadUnreadBadge`: that one is positioned for a
 * thread row's corner, and here the count sits inline beside the word.
 *
 * `--brand-danger`, matching every OTHER unread badge in the chat (2026-09-20).
 * It was gold-on-charcoal, which made "unread" mean two different colours on
 * one screen: the rail rows said red, the tabs above them said gold.
 */
const TabBadge = styled.span`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 8px;
  background: var(--brand-danger);
  color: #ffffff;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
`;

/** The digits. Hidden — not removed — when the badge becomes a dot. */
const TabBadgeCount = styled.span`
  display: inline;
`;

/**
 * The gliding active pill. One element for three tabs, so switching reads as
 * the selection MOVING rather than one box going out and another coming in.
 *
 * MEASURED, not stepped: the tabs are content-sized (see `Tab`), so they are
 * three different widths — four in Spanish — and a `translateX(index * 100%)`
 * thumb would sit under the wrong word. Its geometry arrives as an inline
 * style from a layout effect; until then it is invisible and the active tab
 * paints its own background, which is byte-for-byte the old rendering.
 */
const TabThumb = styled.span`
  position: absolute;
  top: 2px;
  bottom: 2px;
  left: 0;
  border-radius: 7px;
  background: rgba(198, 167, 94, 0.18);
  border: 1px solid rgba(198, 167, 94, 0.5);
  pointer-events: none;
  /* The smooth house curve, never the spring: a selection that overshoots the
     word you clicked and comes back reads as a glitch. */
  transition: transform 0.2s cubic-bezier(0.2, 0.8, 0.2, 1),
              width 0.2s cubic-bezier(0.2, 0.8, 0.2, 1),
              opacity 0.14s ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

/** The track the thumb slides in. Carries the tablist role and the keys. */
const TabsTrack = styled.div`
  position: relative;
  display: flex;
  align-items: stretch;
  gap: 2px;
  padding: 2px;
  border: 1px solid rgba(198, 167, 94, 0.2);
  border-radius: 9px;
  background: rgba(198, 167, 94, 0.04);
`;

const Tab = styled.button<{ $active: boolean; $thumbed: boolean }>`
  /* EQUAL SHARES WITH A FLOOR (Miles, 2026-09-20, round 2: the three must be
     "equally spaced and always centered in the space they're given").

     The two halves are both load-bearing and neither works alone:

       flex: 1 1 0        — every tab takes the same share of the track, so
                            the strip is filled evenly instead of the labels
                            packing to the left with the slack on the right.
       min-width: min-content
                          — but a share is a FLOOR, never a squeeze: flex may
                            not shrink a tab below its own content. This is
                            what keeps the word whole.

     Round 1 had the same first half with NO floor (min-width: 0), so a count
     ate the label and "mentions" became "menti…". Fixing that by dropping the
     grow cured the truncation and left the tabs huddled at the left edge,
     which is what this round is about. The floor is the fix; the grow was
     never the problem. */
  flex: 1 1 0;
  min-width: min-content;
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  /* The 30px floor is the density pass; a coarse pointer keeps the 32px touch
     target, and so does the sheet, which never sees the rail's variables. */
  min-height: var(--rail-tab-min-height, 32px);
  /* 7px, not 9: measured, it is what lets both counts show at the rail's
     default width in Spanish as well as English. */
  padding: 5px 7px;
  border: 1px solid transparent;
  border-radius: 7px;
  /* While the measured thumb is drawing the selection, the button draws
     nothing; before it is measured the button is the selection, exactly as
     it has always been. */
  background: ${(p) => (p.$active && !p.$thumbed ? 'rgba(198, 167, 94, 0.16)' : 'transparent')};
  border-color: ${(p) => (p.$active && !p.$thumbed ? 'rgba(198, 167, 94, 0.5)' : 'transparent')};
  color: ${(p) => (p.$active ? 'var(--brand-gold)' : 'rgba(255, 255, 255, 0.62)')};
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 11.5px;
  font-weight: ${(p) => (p.$active ? 600 : 500)};
  letter-spacing: 0.02em;
  cursor: pointer;
  transition: color 0.15s ease, background 0.15s ease, border-color 0.15s ease;

  &:hover {
    color: var(--brand-gold);
  }

  &:focus-visible {
    outline: 2px solid rgba(198, 167, 94, 0.6);
    outline-offset: 1px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const TabLabel = styled.span`
  /* The ellipsis is a backstop, never the plan: the button's own
     min-width: min-content is what keeps the word whole. */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/**
 * Too little room for a word AND a number: the number becomes a dot and the
 * word stays whole. The badge keeps its aria-label, so the count is still
 * spoken — nothing is lost except the digits nobody could have read beside a
 * truncated label anyway.
 */
const NarrowBadgeRules = `
  @container thread-tabs (max-width: ${TAB_BADGE_DOT_MAX_CONTAINER_PX}px) {
    & ${TabBadge} {
      min-width: 7px;
      width: 7px;
      height: 7px;
      padding: 0;
      border-radius: 50%;
    }
    & ${TabBadgeCount} {
      display: none;
    }
    /* The tabs give up their side padding at the same moment. Measured:
       Spanish at the rail's 220px minimum, with a count on two of the three
       tabs, overflowed the track even once the counts were dots — and the
       rail clips, so the third tab was losing its edge. Taking 3px a side
       off each tab returns 18px, which is more than the 5px it was over. */
    & ${Tab} {
      padding-left: 4px;
      padding-right: 4px;
      gap: 3px;
    }
  }
`;

export function formatTabBadge(n: number): string {
  if (n > TAB_BADGE_CAP) return `${TAB_BADGE_CAP}+`;
  return String(n);
}

/** Where the gliding pill sits. `null` until the strip has been laid out. */
interface ThumbRect { left: number; width: number }

export const ThreadListTabs: React.FC<ThreadListTabsProps> = ({
  active,
  onSelect,
  mentionUnread = 0,
  directUnread = 0,
  onMarkAllRead,
  variant = 'rail',
  lang = 'en',
}) => {
  const copy = threadListCopy(lang);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [thumb, setThumb] = useState<ThumbRect | null>(null);

  const label: Record<ThreadListTab, string> = {
    threads: copy.tabThreads,
    mentions: copy.tabMentions,
    direct: copy.tabDirect,
  };
  const unread: Record<ThreadListTab, number> = {
    threads: 0,
    mentions: Math.max(0, Math.floor(mentionUnread)),
    direct: Math.max(0, Math.floor(directUnread)),
  };

  /**
   * Measure the active button and put the pill under it.
   *
   * A layout effect, so the pill is in place before the browser paints and the
   * first frame never shows it at the wrong tab. Everything that can change a
   * tab's width re-measures: the selection, the labels (a language change),
   * the counts (a badge appears, or shrinks to a dot), and — since the rail is
   * draggable — the strip's own size, through a ResizeObserver.
   *
   * Zero geometry (jsdom, a display:none ancestor, a first paint before fonts)
   * yields `null`, and `null` means "no pill": the active button paints its own
   * background instead, which is exactly what this strip did before the pill
   * existed. So the unmeasured case is not a degraded rendering, it is the old
   * one.
   */
  const measure = useCallback(() => {
    const track = trackRef.current;
    const el = track?.querySelector<HTMLButtonElement>(`[data-thread-tab="${active}"]`);
    if (!track || !el) { setThumb(null); return; }
    const width = el.offsetWidth;
    if (!width) { setThumb(null); return; }
    setThumb((current) => {
      const next = { left: el.offsetLeft, width };
      return current && current.left === next.left && current.width === next.width ? current : next;
    });
  }, [active]);

  useLayoutEffect(() => { measure(); }, [measure, label.threads, label.mentions, label.direct, unread.mentions, unread.direct]);

  useEffect(() => {
    const track = trackRef.current;
    if (!track || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => measure());
    ro.observe(track);
    return () => ro.disconnect();
  }, [measure]);

  // Arrow/Home/End across the strip, the roving-tablist contract every other
  // list control here already honours (the rail's own rows, the search combobox).
  const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
    if (!keys.includes(e.key)) return;
    e.preventDefault();
    const index = THREAD_LIST_TABS.indexOf(active);
    const last = THREAD_LIST_TABS.length - 1;
    let next = index;
    if (e.key === 'ArrowLeft') next = index <= 0 ? last : index - 1;
    else if (e.key === 'ArrowRight') next = index >= last ? 0 : index + 1;
    else if (e.key === 'Home') next = 0;
    else next = last;
    const target = THREAD_LIST_TABS[next];
    onSelect(target);
    const el = trackRef.current?.querySelector<HTMLButtonElement>(`[data-thread-tab="${target}"]`);
    try { el?.focus(); } catch { /* not mounted yet — selection still moved */ }
  }, [active, onSelect]);

  return (
    <TabsRow>
      <TabsTrack
        ref={trackRef}
        role="tablist"
        aria-label={copy.tabsLabel}
        aria-orientation="horizontal"
        data-thread-list-tabs={variant}
        onKeyDown={onKeyDown}
      >
        <TabThumb
          aria-hidden="true"
          data-thread-tab-thumb={thumb ? 'true' : 'false'}
          style={thumb
            ? { opacity: 1, width: `${thumb.width}px`, transform: `translateX(${thumb.left}px)` }
            : { opacity: 0, width: 0, transform: 'translateX(0)' }}
        />
        {THREAD_LIST_TABS.map((tab) => {
          const isActive = tab === active;
          const count = unread[tab];
          return (
            <Tab
              key={tab}
              type="button"
              role="tab"
              id={`thread-list-tab-${tab}`}
              data-thread-tab={tab}
              data-active={isActive ? 'true' : 'false'}
              $active={isActive}
              $thumbed={thumb !== null}
              aria-selected={isActive}
              aria-controls="thread-list-panel"
              // Only the selected tab is in the tab order; the arrows move within.
              tabIndex={isActive ? 0 : -1}
              onClick={() => onSelect(tab)}
              onContextMenu={tab === 'mentions' && onMarkAllRead && count > 0
                ? (e) => { e.preventDefault(); onMarkAllRead(); }
                : undefined}
            >
              <TabLabel>{label[tab]}</TabLabel>
              {count > 0 && (
                <TabBadge data-thread-tab-badge={tab} aria-label={copy.tabUnreadCount(count)}>
                  <TabBadgeCount aria-hidden="true">{formatTabBadge(count)}</TabBadgeCount>
                </TabBadge>
              )}
            </Tab>
          );
        })}
      </TabsTrack>
    </TabsRow>
  );
};

export default ThreadListTabs;
